import { readAuthSession } from '../services/authSession';

const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

function extractOrders(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return (
    payload?.data?.orders ||
    payload?.data?.items ||
    payload?.orders ||
    payload?.items ||
    []
  );
}

function pickImage(...candidates) {
  for (const value of candidates) {
    const url = String(value || '').trim();
    if (!url) continue;
    if (
      url.startsWith('http://') ||
      url.startsWith('https://') ||
      url.startsWith('data:') ||
      url.startsWith('blob:') ||
      url.startsWith('/')
    ) {
      return url;
    }
  }
  return '';
}

/** e.g. { dateLine: "20 NOV,", timeLine: "10:34PM" } */
function formatOrderTime(value) {
  if (!value) return { dateLine: '—', timeLine: '' };
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return { dateLine: '—', timeLine: '' };

  const day = String(date.getDate()).padStart(2, '0');
  const month = date
    .toLocaleString('en-US', { month: 'short' })
    .replace('.', '')
    .toUpperCase();
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const hour12 = hours % 12 || 12;
  const ampm = hours >= 12 ? 'PM' : 'AM';

  return {
    dateLine: `${day} ${month},`,
    timeLine: `${hour12}:${minutes}${ampm}`,
  };
}

function normalizeStatus(status) {
  const raw = String(status || 'pending').trim().toLowerCase().replace(/_/g, ' ');
  if (!raw) return 'Pending';
  if (raw === 'completed' || raw === 'delivered' || raw === 'done') return 'Completed';
  if (raw === 'processing' || raw === 'in progress' || raw === 'inprocess') return 'Processing';
  if (raw === 'pending' || raw === 'awaiting' || raw === 'unpaid') return 'Pending';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function statusTone(label) {
  const key = String(label || '').toLowerCase();
  if (key === 'completed' || key === 'delivered') return 'completed';
  if (key === 'processing' || key === 'in progress') return 'processing';
  return 'pending';
}

/** Flatten API orders into table rows matching the kiosk order-status UI. */
export function flattenOrderRows(orders) {
  const list = Array.isArray(orders) ? orders : [];
  const rows = [];

  list.forEach((order, orderIndex) => {
    const orderedAt = order?.createdAt || order?.created_at || order?.orderedAt || '';
    const { dateLine, timeLine } = formatOrderTime(orderedAt);
    const status = normalizeStatus(order?.status || order?.order_status || order?.orderStatus);
    const tone = statusTone(status);
    const artSubtitle =
      String(
        order?.favourite?.type ||
          order?.artType ||
          order?.art_type ||
          order?.station ||
          order?.type ||
          ''
      ).trim() || '';

    const packName = order?.pack_name || order?.pack?.name;
    if (order?.pack_id || packName) {
      rows.push({
        id: String(order?.id || order?.orderNumber || `pack-${orderIndex}`),
        dateLine,
        timeLine,
        name: String(packName || 'Pack'),
        subtitle: artSubtitle,
        quantity: 1,
        status,
        tone,
        imageUrl: pickImage(
          order?.image_url,
          order?.pack?.image,
          order?.pack?.picture,
          order?.items?.[0]?.product?.picture
        ),
      });
      return;
    }

    const items = Array.isArray(order?.items) ? order.items : [];
    if (!items.length) {
      rows.push({
        id: String(order?.id || order?.orderNumber || `order-${orderIndex}`),
        dateLine,
        timeLine,
        name: String(order?.orderNumber ? `Order #${order.orderNumber}` : 'Order'),
        subtitle: artSubtitle,
        quantity: Number(order?.quantity || 1),
        status,
        tone,
        imageUrl: pickImage(order?.image_url, order?.imageUrl),
      });
      return;
    }

    items.forEach((line, lineIndex) => {
      const product = line?.product || line || {};
      const name = String(product?.name || line?.name || 'Product').trim() || 'Product';
      const subtitle =
        String(
          line?.favourite?.type ||
            line?.artType ||
            line?.type ||
            artSubtitle ||
            ''
        ).trim() || '';
      rows.push({
        id: String(
          line?.id || `${order?.id || order?.orderNumber || orderIndex}-${lineIndex}`
        ),
        dateLine,
        timeLine,
        name,
        subtitle,
        quantity: Number(line?.quantity || 1),
        status,
        tone,
        imageUrl: pickImage(
          line?.image_url,
          line?.artImage,
          line?.favouriteImg,
          product?.picture,
          product?.image,
          order?.image_url
        ),
      });
    });
  });

  return rows;
}

/**
 * GET /product/my-orders for the signed-in user (Bearer token).
 * Falls back to ?userId= when a ticket-linked user id is available without a token.
 */
export async function getMyOrdersApi({ userId } = {}) {
  if (!apiBaseUrl) {
    throw new Error('API base URL is not set.');
  }

  const auth = readAuthSession();
  const token = auth?.token || '';
  const resolvedUserId = userId || auth?.user?.id || '';

  if (!token && !resolvedUserId) {
    return { orders: [], rows: [] };
  }

  const params = new URLSearchParams();
  if (resolvedUserId) params.set('userId', String(resolvedUserId));

  const headers = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const query = params.toString();
  const url = `${apiBaseUrl}/product/my-orders${query ? `?${query}` : ''}`;
  const response = await fetch(url, { method: 'GET', headers });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.message || `Orders failed (${response.status})`);
    error.response = { data: payload, status: response.status };
    throw error;
  }

  const orders = extractOrders(payload);
  return { orders, rows: flattenOrderRows(orders), raw: payload };
}
